import React, { useEffect, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { LoginPage } from './LoginPage';
import { DashboardRoute } from '../routes/DashboardRoute';
import { MobilePickupPage } from '../pages/MobilePickupPage';
import ScanJobPage from '../pages/ScanJobPage';
import MobileJobSummaryPage from '../pages/MobileJobSummaryPage';
import MobileJobPage from '../pages/MobileJobPage';
import { useSignalRConnection, useSignalREvent } from '../hooks/useSignalR';
import { fetchMe } from '../services/meApi';
import { bootstrapAppSettings } from '../hooks/useAppSettings';
import { CurrentUserProvider } from '../contexts/CurrentUserContext';
import { setLineItemsSyncRanAt } from '../services/lineItemsSyncStatus';
import type {
  CurrentUser,
  LineItemsSyncRanPayload,
  UserPreferences,
  UserRole,
} from '@techgitter/contracts';

interface LegacyUserInfo {
  displayName: string;
  userPrincipalName: string;
  roles: string[];        // lowercase, for back-compat with existing prop-drilled consumers
}

/**
 * Map canonical contract roles (PascalCase) to the lowercase strings that the
 * legacy `userInfo.roles.includes('admin')` call sites expect. Used only for
 * the back-compat prop; new components should call `useCurrentUser().hasRole`.
 */
function toLegacyRoles(roles: UserRole[]): string[] {
  return roles.map((r) => r.toLowerCase());
}

export const AuthenticatedApp: React.FC = () => {
  const { accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [legacyUserInfo, setLegacyUserInfo] = useState<LegacyUserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [unprovisioned, setUnprovisioned] = useState<{ email: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Start SignalR connection once authenticated and authorized
  // Start the SignalR connection once authenticated and authorized. The
  // connection state is no longer surfaced in the header, so the return value
  // is intentionally unused — the call is kept for its connection side-effect.
  useSignalRConnection({
    enabled: isAuthenticated && (currentUser?.roles.length ?? 0) > 0,
  });

  useSignalREvent('jobs:line-items-sync-ran', (data) => {
    setLineItemsSyncRanAt((data as LineItemsSyncRanPayload).ranAt);
  });

  const handlePreferencesChange = React.useCallback((preferences: UserPreferences) => {
    setCurrentUser((user) => (user ? { ...user, preferences } : user));
  }, []);

  const loadIdentity = React.useCallback(async (signal: AbortSignal) => {
    setLoading(true);
    setError(null);
    setUnprovisioned(null);
    try {
      const me = await fetchMe(signal);
      if (!me) {
        // Backend says authenticated to AD but not provisioned in cr001_userses
        setUnprovisioned({ email: accounts[0]?.username || '(unknown)' });
        setCurrentUser(null);
        setLegacyUserInfo(null);
        return;
      }
      setCurrentUser(me);
      // index.tsx warms the settings cache before render, which in production can
      // be before sign-in has finished — that call 401s and leaves module-level
      // readers (business hours, product codes) on their defaults. Re-warm now
      // that a token exists; a no-op when the first call already succeeded.
      bootstrapAppSettings().catch(() => {
        /* fall back to defaults — error already logged by the fetcher */
      });
      setLegacyUserInfo({
        displayName: me.displayName,
        userPrincipalName: me.email,
        roles: toLegacyRoles(me.roles),
      });
    } catch (e) {
      if ((e as DOMException)?.name === 'AbortError') return;
      console.error('Failed to load /api/me:', e);
      setError(e instanceof Error ? e.message : String(e));
      setCurrentUser(null);
      setLegacyUserInfo(null);
    } finally {
      setLoading(false);
    }
  }, [accounts]);

  useEffect(() => {
    if (isAuthenticated && accounts.length > 0) {
      const controller = new AbortController();
      loadIdentity(controller.signal);
      return () => controller.abort('useEffect cleanup');
    }
    if (isAuthenticated && accounts.length === 0) {
      console.error('Authenticated but no accounts found');
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, accounts, loadIdentity]);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  if (unprovisioned) {
    return (
      <div className="unauthorized">
        <h2>Account not provisioned</h2>
        <p>Hello {unprovisioned.email}</p>
        <p>
          Your AD account is authenticated, but it isn't set up in SM4RT yet.
          An administrator needs to add you to the Users table and grant the
          appropriate roles before you can use the application.
        </p>
        <button type="button" onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="unauthorized">
        <h2>Couldn't load your profile</h2>
        <p>The backend returned an error while resolving your account:</p>
        <pre style={{ whiteSpace: 'pre-wrap', textAlign: 'left' }}>{error}</pre>
        <button type="button" onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  if (currentUser && currentUser.roles.length === 0) {
    return (
      <div className="unauthorized">
        <h2>No roles assigned</h2>
        <p>Hello {currentUser.displayName} ({currentUser.email})</p>
        <p>
          Your account exists in SM4RT but has no roles assigned. An admin must
          grant you at least one role (Technician, Senior Technician, Accounts,
          Manager, Purchasing, or Admin) before you can use the application.
        </p>
        <button type="button" onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <CurrentUserProvider value={currentUser} onPreferencesChange={handlePreferencesChange}>
      <BrowserRouter>
        <Routes>
          {/* Mobile-first scan surfaces — mounted outside MainLayout so they
              carry no desktop chrome (see docs/qr-scan-workflow-requirements.md §5G). */}
          <Route path="/pickup/:ticketNumber" element={<MobilePickupPage />} />
          {/* The job label's QR — redirects by the job's live state (§9). */}
          <Route path="/j/:ticketNumber" element={<ScanJobPage />} />
          <Route path="/job/:ticketNumber/summary" element={<MobileJobSummaryPage />} />
          <Route path="/job/:ticketNumber" element={<MobileJobPage />} />
          <Route
            path="/*"
            element={
              <DashboardRoute userInfo={legacyUserInfo} />
            }
          />
        </Routes>
      </BrowserRouter>
    </CurrentUserProvider>
  );
};
